import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Zap, Music, Waves, BarChart3, Wind, RefreshCw } from 'lucide-react';
import { API_BASE as API } from '../../lib/apiBase';

const CATEGORY_ICONS = {
  structure: BarChart3,
  energy: Zap,
  rhythm: Music,
  melody: Waves,
  atmosphere: Wind,
};

const CATEGORY_COLORS = {
  structure: '#F5C842',
  energy: '#FF6B00',
  rhythm: '#7C3AED',
  melody: '#00D4FF',
  atmosphere: '#10B981',
};

export default function IntentEngine({ sessionId, sections, loopRegion, stages, token, onExecute }) {
  const [intents, setIntents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(null);
  const fetchRef = useRef(null);

  const fetchIntents = useCallback(async () => {
    if (!sessionId || !token || !stages?.base_done) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/producer/intents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          session_id: sessionId,
          sections: sections?.map(s => ({ type: s.type, bars: s.bars })) || [],
          loop_region: loopRegion ? { start_pct: loopRegion.startPct, end_pct: loopRegion.endPct } : null,
        }),
      });
      if (r.ok) {
        const data = await r.json();
        setIntents(data.intents || []);
      }
    } catch {}
    finally { setLoading(false); }
  }, [sessionId, token, sections, loopRegion, stages]);

  // Fetch intents when context changes (debounced 2s)
  useEffect(() => {
    if (!stages?.base_done) return;
    if (fetchRef.current) clearTimeout(fetchRef.current);
    fetchRef.current = setTimeout(fetchIntents, 2000);
    return () => { if (fetchRef.current) clearTimeout(fetchRef.current); };
  }, [fetchIntents]);

  const handleIntent = async (intent) => {
    setExecuting(intent.id);
    try {
      if (onExecute) await onExecute(intent.instruction);
    } catch {}
    setExecuting(null);
  };

  if (!stages?.base_done) return null;

  if (loading && intents.length === 0) {
    return (
      <div className="flex gap-1.5" data-testid="intent-engine-loading">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-5 w-16 rounded-md bg-[#1A1A1F] animate-pulse" />
        ))}
      </div>
    );
  }

  if (intents.length === 0) return null;

  return (
    <div className="flex items-center gap-1 overflow-x-auto producer-scroll pb-0.5" data-testid="intent-engine">
      {intents.map((intent) => {
        const Icon = CATEGORY_ICONS[intent.category] || Zap;
        const color = CATEGORY_COLORS[intent.category] || '#F5C842';
        const isExec = executing === intent.id;

        return (
          <button
            key={intent.id}
            onClick={() => handleIntent(intent)}
            disabled={!!executing}
            data-testid={`intent-${intent.id}`}
            title={intent.description}
            className={`
              flex items-center gap-1 px-1.5 py-1 rounded-md text-[8px] sm:text-[9px] font-medium whitespace-nowrap
              border transition-all
              ${isExec
                ? 'border-[#F5C842]/60 bg-[#F5C84220] text-[#F5C842]'
                : 'border-[#27272A] bg-[#0F0F12] text-[#A1A1AA] hover:border-[#3F3F46] hover:text-white'}
              ${executing && !isExec ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            {isExec ? (
              <RefreshCw size={9} className="animate-spin" style={{ color }} />
            ) : (
              <Icon size={9} style={{ color }} />
            )}
            {intent.label}
          </button>
        );
      })}
      <button
        onClick={fetchIntents}
        disabled={loading}
        data-testid="intent-refresh"
        className="w-5 h-5 rounded-md border border-[#27272A] bg-[#0F0F12] flex items-center justify-center text-[#52525B] hover:text-white hover:border-[#3F3F46] transition-all"
        title="Nuevas sugerencias"
      >
        <RefreshCw size={8} className={loading ? 'animate-spin' : ''} />
      </button>
    </div>
  );
}
